import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle, ShieldCheck, X } from 'lucide-react';

/*
  ConfirmDialog — confirmación propia del panel de administración (sustituye a window.confirm/prompt).

  Props:
  - title, description (texto o nodo)
  - details: lista de líneas que dicen exactamente qué va a pasar. Cada línea es un texto o { label, value }.
  - tone: 'danger' (rojo, acciones destructivas) | 'gold' (acciones normales)
  - confirmLabel, cancelLabel
  - requireText: palabra que hay que escribir para habilitar el botón (confirmación fuerte, p. ej. REEMPLAZAR)
  - input: { label, placeholder, defaultValue, required, type, multiline, hint } → pide un dato; onConfirm recibe el valor
  - onConfirm(value), onClose()

  Uso cómodo con el hook:
    const [dialog, ask] = useConfirm();
    const ok = await ask({ title: '…', details: [...] });   // false si cancela; true o el valor escrito si confirma
    …
    return <>{dialog}…</>;
*/
export default function ConfirmDialog({
  title, description, details, tone = 'danger', confirmLabel = 'Confirmar', cancelLabel = 'Cancelar',
  requireText, input, children, onConfirm, onClose,
}) {
  const id = useId();
  const [typed, setTyped] = useState('');
  const [value, setValue] = useState(input?.defaultValue ?? '');
  const firstRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => firstRef.current?.focus(), 30);
    // En captura y sin propagar: Esc cierra solo este diálogo, no la ficha (Drawer) que haya debajo
    const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
    window.addEventListener('keydown', onKey, true);
    return () => { clearTimeout(t); window.removeEventListener('keydown', onKey, true); };
  }, [onClose]);

  const textOk = !requireText || typed.trim().toUpperCase() === String(requireText).toUpperCase();
  const inputOk = !input || !input.required || String(value).trim() !== '';
  const ok = textOk && inputOk;
  const danger = tone === 'danger';
  const Icon = danger ? AlertTriangle : ShieldCheck;
  const submit = (e) => { e.preventDefault(); if (ok) onConfirm(input ? String(value).trim() : true); };
  const fieldCls = 'w-full bg-white/[0.04] border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-[#c9a84c]/50';

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/65 p-0 sm:p-4" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <motion.form
        onSubmit={submit}
        role="alertdialog" aria-modal="true" aria-labelledby={`${id}-t`} aria-describedby={`${id}-d`}
        initial={{ opacity: 0, y: 16, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ duration: 0.18 }}
        className="w-full sm:max-w-md max-h-[92vh] overflow-y-auto bg-[#0d1829] border border-white/10 rounded-t-2xl sm:rounded-2xl p-5 sm:p-6 shadow-2xl"
        data-testid="confirm-dialog"
      >
        <div className="flex items-start gap-3">
          <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${danger ? 'bg-red-500/10 text-red-300' : 'bg-[#c9a84c]/10 text-[#c9a84c]'}`}>
            <Icon size={17} />
          </div>
          <div className="min-w-0 flex-1">
            <h3 id={`${id}-t`} className="text-white font-semibold text-[15px] leading-snug">{title}</h3>
            {description && <div id={`${id}-d`} className="text-sm text-gray-400 mt-1">{description}</div>}
          </div>
          <button type="button" onClick={onClose} aria-label="Cerrar" className="p-1.5 -mr-1.5 -mt-1 rounded-lg text-gray-400 hover:text-white hover:bg-white/[0.05]"><X size={16} /></button>
        </div>

        {details?.length > 0 && (
          <ul className="mt-4 space-y-1.5 rounded-xl border border-white/[0.06] bg-white/[0.02] p-3 text-sm">
            {details.filter(Boolean).map((d, i) => (typeof d === 'object' && d !== null && 'label' in d ? (
              <li key={i} className="flex flex-wrap justify-between gap-x-3 gap-y-0.5">
                <span className="text-gray-400">{d.label}</span>
                <span className="text-white font-medium text-right tabular-nums">{d.value}</span>
              </li>
            ) : (
              <li key={i} className="flex gap-2 text-gray-300"><span className="text-[#c9a84c] select-none">·</span><span className="min-w-0">{d}</span></li>
            )))}
          </ul>
        )}

        {children}

        {input && (
          <label className="block mt-4">
            <span className="text-[11px] uppercase tracking-wider text-gray-400 font-semibold">{input.label}</span>
            {input.multiline ? (
              <textarea ref={firstRef} rows={3} value={value} onChange={(e) => setValue(e.target.value)} placeholder={input.placeholder}
                className={`${fieldCls} mt-1 resize-none`} />
            ) : (
              <input ref={firstRef} type={input.type || 'text'} value={value} onChange={(e) => setValue(e.target.value)} placeholder={input.placeholder}
                className={`${fieldCls} mt-1`} />
            )}
            {input.hint && <span className="block text-xs text-gray-400 mt-1">{input.hint}</span>}
          </label>
        )}

        {requireText && (
          <label className="block mt-4">
            <span className="text-sm text-gray-300">Escribe <strong className="text-white tracking-wide">{requireText}</strong> para confirmar</span>
            <input ref={input ? undefined : firstRef} value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" spellCheck={false}
              aria-label={`Escribe ${requireText} para confirmar`} className={`${fieldCls} mt-1 uppercase`} data-testid="confirm-dialog-text" />
          </label>
        )}

        <div className="mt-5 flex flex-col-reverse sm:flex-row gap-2 sm:gap-3">
          <button type="button" onClick={onClose}
            className="flex-1 min-h-[44px] sm:min-h-[40px] rounded-xl border border-white/10 text-gray-300 text-sm hover:bg-white/[0.04]">
            {cancelLabel}
          </button>
          <button type="submit" disabled={!ok} ref={!input && !requireText ? firstRef : undefined}
            className={`flex-1 min-h-[44px] sm:min-h-[40px] rounded-xl text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed ${danger
              ? 'bg-red-500/20 border border-red-500/35 text-red-200 hover:bg-red-500/30'
              : 'bg-[#c9a84c] text-[#0a1628] hover:bg-[#d4b85c]'}`}
            data-testid="confirm-dialog-ok">
            {confirmLabel}
          </button>
        </div>
      </motion.form>
    </div>
  );
}

// Hook: devuelve [elemento, ask(opciones)]. ask resuelve false (cancelado), true o el valor escrito.
export function useConfirm() {
  const [opts, setOpts] = useState(null);
  const resolver = useRef(null);
  const ask = useCallback((o) => new Promise((resolve) => {
    resolver.current = resolve;
    setOpts(o);
  }), []);
  const close = useCallback(() => { setOpts(null); resolver.current?.(false); resolver.current = null; }, []);
  const confirm = useCallback((v) => { setOpts(null); resolver.current?.(v); resolver.current = null; }, []);
  const element = opts ? <ConfirmDialog {...opts} onClose={close} onConfirm={confirm} /> : null;
  return [element, ask];
}
